import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, X } from "lucide-react";
import { usePlan } from "../../context/PlanContext";
import { PageLoader } from "../components/PageLoader";
import { ConfirmDialog } from "../components/crm/ui";
import { AccessNotice, fetchApi, reasonOf } from "../components/marketing/access";
import { Toast } from "../components/wallet/parts";
import { NO_DATA } from "../lib/format";

/**
 * My subscription (/subscription/manage), the old pages/subscription/SubscriptionPage.js in the Olli
 * design. Same data and the one action it had:
 *   plan, status, billing cycle, period dates, modules ... PlanContext (GET /api/subscription/current)
 *   usage this period ..................................... GET /api/subscription/usage { slug: { name, used, limit } }
 *   cancel ................................................ POST /api/subscription/cancel (access runs to the period end)
 *   wallet credit of the plan ............................. GET /api/subscription/plans → wallet_credit { amount, currency }
 *                                                           (added on each payment; the line is left out if plans don't load)
 * Change plan goes to /subscription/plans. A yearly plan runs 14 months (12 paid + 2 free), so the
 * renewal date shown is the server's, not a year from the start.
 */
const day = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : NO_DATA);
const CYCLE = { monthly: "Monthly", quarterly: "Quarterly", annual: "Yearly" };
const creditLabel = (c) => {
  if (!c || !(Number(c.amount) > 0)) return null;
  const code = String(c.currency || "USD").toUpperCase();
  return new Intl.NumberFormat(code === "INR" ? "en-IN" : "en-US", { style: "currency", currency: code, maximumFractionDigits: Number.isInteger(Number(c.amount)) ? 0 : 2 }).format(Number(c.amount));
};

export default function MySubscription() {
  const planCtx = usePlan();
  const sub = planCtx?.getSubscription?.() || null;
  const modules = planCtx?.planData?.modules || null;
  const [usage, setUsage] = useState(null);
  const [usageError, setUsageError] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const loadUsage = useCallback(async () => {
    setUsageError(null);
    const r = await fetchApi("/api/subscription/usage");
    if (!r.ok) { setUsage({}); setUsageError(reasonOf(r)); return; }
    setUsage(r.data?.data || {});
  }, []);
  useEffect(() => { loadUsage(); }, [loadUsage]);
  const [plans, setPlans] = useState(null);
  useEffect(() => { fetchApi("/api/subscription/plans").then((r) => setPlans(r.ok ? r.data?.data || [] : [])); }, []);

  const cancel = async () => {
    setConfirm(false);
    setCancelling(true); setError("");
    const r = await fetchApi("/api/subscription/cancel", { method: "post" });
    setCancelling(false);
    if (!r.ok) { setError(r.message || "Couldn't cancel the subscription. Try again."); return; }
    setToast(r.data?.message || "Subscription cancelled. Access continues until the end of the billing period.");
    planCtx?.refresh?.();
  };

  if (usage === null && !planCtx?.planData) return <PageLoader label="Loading your subscription…" />;
  const paid = sub && !sub.is_free;
  const cancelled = sub?.status === "cancelled" || !!sub?.cancelled_at;
  const credit = paid ? creditLabel((plans || []).find((p) => p.id === sub.plan_id)?.wallet_credit) : null;

  return (
    <div className="mx-auto flex max-w-5xl animate-fade-in flex-col gap-6 px-6 py-9 md:px-8">
      <header className="flex flex-col gap-3">
        <Link to="/wallet" className="flex w-fit items-center gap-1.5 text-small font-medium text-muted hover:text-ink"><ArrowLeft size={16} strokeWidth={1.75} />Wallet</Link>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><h1 className="text-h1 font-semibold">My subscription</h1><p className="mt-1 text-body text-muted">Your plan, what it includes and what you've used this period</p></div>
          <Link to="/subscription/plans" className="flex h-11 items-center rounded-full bg-sun px-5 text-small font-semibold text-ink hover:brightness-95">{sub ? "Change plan" : "See plans"}</Link>
        </div>
      </header>

      {error && <p role="alert" className="rounded-md bg-danger-soft px-4 py-3 text-small font-medium text-danger">{error}</p>}

      <section aria-label="Current plan" className={`rounded-xl p-6 ${paid ? "bg-ink bg-[image:var(--gradient-night)] text-paper shadow-lift" : "bg-paper shadow-soft"}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><p className={`text-caption font-semibold uppercase tracking-wider ${paid ? "text-paper/60" : "text-muted"}`}>Current plan</p><p className="mt-1 text-h2 font-semibold">{sub?.plan_name || "No plan"}</p></div>
          <span className={`rounded-full px-3 py-1 text-caption font-semibold capitalize ${paid ? "bg-sun text-ink" : "bg-mist text-ink"}`}>{sub?.status || "Inactive"}</span>
        </div>
        {paid && <dl className="mt-5 grid grid-cols-2 gap-4 border-t border-paper/15 pt-5 sm:grid-cols-4">{[["Billing", CYCLE[sub.billing_cycle] || sub.billing_cycle || NO_DATA], ["Started", day(sub.current_period_start)], [cancelled ? "Ends" : "Renews", day(sub.current_period_end)], ["Expires", day(sub.expires_at)]].map(([k, v]) => <div key={k}><dt className="text-caption text-paper/60">{k}</dt><dd className="mt-0.5 text-small font-semibold">{v}</dd></div>)}</dl>}
        {credit && <p className="mt-4 text-small text-paper/75">Each payment adds <span className="font-semibold text-paper">{credit}</span> to your wallet for WhatsApp messages and AI. <Link to="/wallet" className="font-semibold text-sun hover:underline">See wallet</Link></p>}
      </section>

      <section aria-labelledby="usage-title" className="rounded-lg bg-paper p-6 shadow-soft">
        <h2 id="usage-title" className="text-h3 font-semibold">Usage this period</h2>
        {usage === null ? <PageLoader label="Loading usage…" className="py-10" /> : usageError ? <AccessNotice reason={usageError} onRetry={loadUsage} compact className="mt-4" /> : Object.keys(usage).length === 0 ? <p className="mt-3 text-small text-muted">Nothing on your plan is counted by usage.</p> : (
          <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(usage).map(([slug, u]) => {
              const used = Number(u.used) || 0; const limit = Number(u.limit) || 0;
              const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
              const tone = limit && used >= limit ? "bg-danger" : limit && pct >= 80 ? "bg-warning" : "bg-sky";
              return <li key={slug} className="rounded-md bg-mist p-4"><div className="flex items-center justify-between gap-3 text-small"><span className="font-semibold">{u.name || slug}</span><span className="tabular-nums text-muted">{used.toLocaleString("en-IN")}{limit ? ` / ${limit.toLocaleString("en-IN")}` : " · no limit"}</span></div>{limit > 0 && <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-paper"><i className={`block h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} /></div>}</li>;
            })}
          </ul>
        )}
      </section>

      {modules && <section aria-labelledby="modules-title" className="rounded-lg bg-paper p-6 shadow-soft">
        <h2 id="modules-title" className="text-h3 font-semibold">What your plan includes</h2>
        <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">{Object.entries(modules).map(([slug, m]) => { const on = !!(m.enabled || m.is_free_default); return <li key={slug} className="flex items-center gap-2 rounded-sm px-3 py-2 text-small">{on ? <Check size={15} strokeWidth={2.25} className="shrink-0 text-success" /> : <X size={15} strokeWidth={2} className="shrink-0 text-muted" />}<span className={on ? "" : "text-muted"}>{m.name || slug}</span></li>; })}</ul>
      </section>}

      {paid && ["active", "trialing"].includes(sub.status) && !cancelled && <div className="flex justify-center"><button type="button" onClick={() => setConfirm(true)} disabled={cancelling} className="text-small font-semibold text-danger underline-offset-4 hover:underline disabled:opacity-50">{cancelling ? "Cancelling…" : "Cancel subscription"}</button></div>}

      {confirm && <ConfirmDialog title="Cancel your subscription?" body={<p>You keep {sub.plan_name} until {day(sub.current_period_end)}. It won't renew after that.</p>} confirmLabel="Cancel subscription" onCancel={() => setConfirm(false)} onConfirm={cancel} />}
      {toast && <Toast message={toast} onDone={() => setToast("")} />}
    </div>
  );
}
